/**
 * cart-drawer.js
 *
 * Global cart drawer (flyout) custom element.
 *
 * Responsibilities
 * ----------------
 * 1. Open/close + focus trap + Escape to close. Open state lives on <html>
 *    so it survives the section re-render that follows every cart mutation.
 *
 * 2. Cart Ajax API:
 *      - POST /cart/add.js     — add a line
 *      - POST /cart/change.js  — change quantity, swap selling plan (OTP↔S&S),
 *                                or remove (quantity: 0)
 *      - POST /cart/update.js  — apply / remove discount codes via `discount`
 *                                (https://shopify.dev/docs/api/ajax/reference/cart)
 *
 * 3. Re-render — after each mutation we fetch the cart-drawer section via the
 *    Section Rendering API (`?sections=<sectionId>`) and swap the
 *    `<cart-drawer id="cart-drawer">` element's innerHTML.
 *
 * 4. Spinner — only shown after a 250 ms debounce, so transient sub-second
 *    requests stay invisible and the UI feels snappy.
 *
 * 5. Header — updates the header badge by reading the new cart.item_count
 *    (returned by every Cart API call).
 *
 * 6. Public API — dispatch `cart:open` / `cart:close` on document; the drawer
 *    also exposes `window.WillowCart.open()` / `.close()` / `.add(payload)`
 *    for code that prefers method calls over events.
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────
  const SECTION_ID            = 'cart-drawer';
  const DRAWER_SELECTOR       = 'cart-drawer#cart-drawer';
  const HTML_OPEN_CLASS       = 'cart-drawer-open';
  const SPINNER_DEBOUNCE_MS   = 250;
  const FOCUSABLE_SELECTORS   = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // ── Section render cache ────────────────────────────────────────────────
  // Concurrent cart mutations would race the section fetch — we tag each one
  // with a request id so older replies are dropped before they paint stale
  // markup over a newer state.
  let lastRequestId = 0;

  // ── Spinner: debounced ─────────────────────────────────────────────────
  let spinnerTimer = null;
  let spinnerStack = 0;

  function spinnerPush(drawer) {
    spinnerStack += 1;
    if (spinnerTimer) return;
    spinnerTimer = setTimeout(function () {
      drawer.setAttribute('data-loading', 'true');
      spinnerTimer = null;
    }, SPINNER_DEBOUNCE_MS);
  }

  function spinnerPop(drawer) {
    spinnerStack = Math.max(0, spinnerStack - 1);
    if (spinnerStack > 0) return;
    if (spinnerTimer) {
      clearTimeout(spinnerTimer);
      spinnerTimer = null;
    }
    drawer.removeAttribute('data-loading');
  }

  // ── Misc helpers ───────────────────────────────────────────────────────
  // Liquid renders translations into HTML data attributes with `| escape`,
  // which turns `'` into `&#39;`. textContent doesn't decode entities, so we
  // route the string through innerHTML on a detached node to get back the
  // human-readable form before painting it into the error element.
  const decodeEntitiesEl = document.createElement('textarea');
  function decodeEntities(str) {
    if (!str) return '';
    decodeEntitiesEl.innerHTML = str;
    return decodeEntitiesEl.value;
  }

  // ── Cart Ajax helpers ──────────────────────────────────────────────────
  // `credentials: 'same-origin'` is the spec default for fetch, but a few
  // privacy-hardened browsers (Samsung Internet "Smart Anti-Tracking",
  // Firefox Focus, embedded WebViews) don't always honour it the same way.
  // Setting it explicitly guarantees the cart-session cookie is sent.
  async function cartFetch(url, body) {
    const init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetch(url, init);
    const data = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      const err = new Error(data.description || data.message || 'Cart request failed');
      err.status = res.status;
      err.body = data;
      throw err;
    }
    return data;
  }

  function cartAdd(payload)           { return cartFetch('/cart/add.js', payload); }
  function cartChange(payload)        { return cartFetch('/cart/change.js', payload); }
  function cartUpdate(payload)        { return cartFetch('/cart/update.js', payload); }
  function cartGet()                  {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) { return r.json(); });
  }

  // ── Section render ─────────────────────────────────────────────────────
  async function renderDrawer(drawer) {
    const myReqId = ++lastRequestId;
    // Use `?section_id=` (singular) which returns the raw section HTML — more
    // reliable than `?sections=` (plural, JSON-wrapped) because it never asks
    // us to parse a response that could be HTML if the route hits a redirect/
    // cache layer. Anchor to the current path so Shopify resolves it cleanly.
    const path = window.location.pathname || '/';
    const url  = path + '?section_id=' + SECTION_ID;
    const res  = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Section render failed: ' + res.status);
    const html = await res.text();
    if (myReqId !== lastRequestId) return; // newer mutation in flight, drop this paint

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    const fresh = wrapper.querySelector(DRAWER_SELECTOR);
    if (!fresh) return;

    // Preserve open state + loading state across re-render.
    const wasOpen = drawer.getAttribute('aria-hidden') === 'false';
    const wasLoading = drawer.getAttribute('data-loading') === 'true';

    drawer.innerHTML = fresh.innerHTML;
    drawer.className = fresh.className;
    if (wasOpen)    drawer.setAttribute('aria-hidden', 'false');
    if (wasLoading) drawer.setAttribute('data-loading', 'true');

    bindDrawerEvents(drawer);
  }

  function updateHeaderBadge(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = String(itemCount);
      el.setAttribute('data-count', String(itemCount));
    });
  }

  // ── Drawer open / close ────────────────────────────────────────────────
  let lastFocusBeforeOpen = null;

  function openDrawer(drawer) {
    if (!drawer) drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    lastFocusBeforeOpen = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(HTML_OPEN_CLASS);
    requestAnimationFrame(function () {
      const panel = drawer.querySelector('.cart-drawer__panel');
      if (panel) panel.focus({ preventScroll: true });
    });
    document.dispatchEvent(new CustomEvent('cart:opened'));
  }

  function closeDrawer(drawer) {
    if (!drawer) drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(HTML_OPEN_CLASS);
    if (lastFocusBeforeOpen && typeof lastFocusBeforeOpen.focus === 'function') {
      try { lastFocusBeforeOpen.focus(); } catch (e) { /* ignore */ }
    }
    document.dispatchEvent(new CustomEvent('cart:closed'));
  }

  function trapFocus(drawer, event) {
    if (event.key !== 'Tab') return;
    const panel = drawer.querySelector('.cart-drawer__panel');
    if (!panel) return;
    const focusable = panel.querySelectorAll(FOCUSABLE_SELECTORS);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────
  async function publicAdd(payload) {
    const drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    openDrawer(drawer);
    spinnerPush(drawer);
    try {
      const result = await cartAdd(payload);
      await renderDrawer(drawer);
      const cart = await cartGet();
      updateHeaderBadge(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, added: result } }));
      return result;
    } catch (err) {
      console.error('[cart-drawer] add failed', err);
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { error: err } }));
      throw err;
    } finally {
      spinnerPop(drawer);
    }
  }

  // ── Event binding ──────────────────────────────────────────────────────
  function bindDrawerEvents(drawer) {
    // Close: overlay + close buttons
    drawer.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () { closeDrawer(drawer); });
    });

    // Quantity selector
    drawer.querySelectorAll('[data-cart-drawer-qty]').forEach(function (wrap) {
      const item    = wrap.closest('[data-cart-drawer-line]');
      const minus   = wrap.querySelector('[data-cart-drawer-qty-action="decrease"]');
      const plus    = wrap.querySelector('[data-cart-drawer-qty-action="increase"]');
      const valueEl = wrap.querySelector('[data-cart-drawer-qty-value]');
      if (!item || !valueEl) return;
      const maxQty  = Number(drawer.dataset.maxQty || 4);
      let busy = false;

      async function setQty(newQty) {
        if (busy) return;
        if (newQty < 0 || newQty > maxQty) return;
        const current = Number(valueEl.textContent.trim()) || 1;
        if (newQty === current) return;
        busy = true;
        spinnerPush(drawer);
        // Optimistic local update for snappier feel; the section re-render
        // is the source of truth and will overwrite this.
        valueEl.textContent = String(newQty);
        try {
          await cartChange({ id: item.dataset.lineKey, quantity: newQty });
          await renderDrawer(drawer);
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        } catch (err) {
          console.error('[cart-drawer] qty change failed', err);
          valueEl.textContent = String(current);
        } finally {
          spinnerPop(drawer);
          busy = false;
        }
      }

      if (minus) minus.addEventListener('click', function () {
        setQty(Math.max(1, (Number(valueEl.textContent.trim()) || 1) - 1));
      });
      if (plus) plus.addEventListener('click', function () {
        setQty(Math.min(maxQty, (Number(valueEl.textContent.trim()) || 1) + 1));
      });
    });

    // Remove item
    drawer.querySelectorAll('[data-cart-drawer-remove]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        const item = btn.closest('[data-cart-drawer-line]');
        if (!item) return;
        spinnerPush(drawer);
        try {
          await cartChange({ id: item.dataset.lineKey, quantity: 0 });
          await renderDrawer(drawer);
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        } catch (err) {
          console.error('[cart-drawer] remove failed', err);
        } finally {
          spinnerPop(drawer);
        }
      });
    });

    // OTP ↔ S&S toggle (checkbox). Checking applies the first available
    // selling plan; unchecking removes it (selling_plan: null). Either
    // direction goes through /cart/change.js with the current line key.
    //
    // Merge guard — Shopify rekeys the line on selling_plan change. If a line
    // with the same variant + the TARGET plan already exists, Shopify merges
    // them and sums the quantities, silently breaking our per-line cap. We do
    // a server-truth pre-flight against /cart.js (not the optimistic local
    // state, which can lag) and refuse the toggle when the merged line would
    // exceed max_qty.
    drawer.querySelectorAll('[data-cart-drawer-subscribe]').forEach(function (input) {
      const initialChecked = input.checked;
      input.addEventListener('change', async function () {
        const group = input.closest('.cart-drawer__item-group');
        const item  = group && group.querySelector('[data-cart-drawer-line]');
        if (!item) return;
        // Server-rendered guard. If Liquid already marked this line blocked,
        // don't even try — the toggle is disabled, but a synthetic `change`
        // event or assistive tech could still trigger us.
        if (input.disabled || item.dataset.toggleBlocked === 'true') {
          input.checked = initialChecked;
          return;
        }
        const firstPlan = Number(item.dataset.firstSellingPlan || 0);
        // Going to S&S requires a known plan; going back to OTP needs nothing.
        if (input.checked && !firstPlan) {
          input.checked = initialChecked;
          return;
        }
        const valueEl = item.querySelector('[data-cart-drawer-qty-value]');
        const currentQty = valueEl ? Number(valueEl.textContent.trim()) || 1 : 1;
        const maxQty    = Number(drawer.dataset.maxQty || 4);
        const variantId = Number(item.dataset.variantId);
        const lineKey   = item.dataset.lineKey;
        const targetPlanId = input.checked ? firstPlan : null;

        spinnerPush(drawer);
        try {
          // ── Backend-truth merge guard ───────────────────────────────────
          // Read the canonical cart and sum any same-variant lines that share
          // the TARGET plan — those are the lines Shopify will fold this one
          // into. If folding pushes past the cap, abort and surface the
          // inline lock message.
          let canonical;
          try {
            canonical = await cartGet();
          } catch (e) {
            canonical = null;
          }
          if (canonical && Array.isArray(canonical.items)) {
            const siblingQty = canonical.items.reduce(function (sum, line) {
              if (line.key === lineKey) return sum;
              if (Number(line.variant_id) !== variantId) return sum;
              const linePlan = line.selling_plan_allocation
                ? Number(line.selling_plan_allocation.selling_plan.id)
                : null;
              return linePlan === targetPlanId ? sum + line.quantity : sum;
            }, 0);
            if (siblingQty > 0 && (siblingQty + currentQty) > maxQty) {
              // Defensive rollback + inline lock state. We re-render via the
              // Section Rendering API so the Liquid-computed `toggle_blocked`
              // banner appears (the server is the source of truth for the
              // warning copy and lock icon styling).
              input.checked = initialChecked;
              item.setAttribute('data-toggle-blocked', 'true');
              const extra = group.querySelector('.cart-drawer__item-extra');
              if (extra) extra.classList.add('cart-drawer__item-extra--locked');
              // Inject the warning element if it's not already there, so the
              // customer sees the explanation even before the next re-render.
              if (!item.querySelector('[data-cart-drawer-toggle-warning]')) {
                const content = item.querySelector('.cart-drawer__item-content');
                if (content) {
                  const msg = decodeEntities(drawer.dataset.maxQtyReached) || 'Maximum reached for this product.';
                  const p = document.createElement('p');
                  p.className = 'cart-drawer__item-lock';
                  p.setAttribute('data-cart-drawer-toggle-warning', '');
                  p.innerHTML =
                    '<svg class="cart-drawer__item-lock-icon" viewBox="0 0 14 16" width="14" height="16" aria-hidden="true" focusable="false">' +
                    '<path d="M3 7V4.5a4 4 0 0 1 8 0V7" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/>' +
                    '<rect x="2" y="7" width="10" height="8" rx="1.5" stroke="currentColor" stroke-width="1.4" fill="none"/>' +
                    '</svg>' +
                    '<span class="cart-drawer__item-lock-text"></span>';
                  p.querySelector('.cart-drawer__item-lock-text').textContent = msg;
                  content.appendChild(p);
                }
              }
              input.disabled = true;
              return;
            }
          }

          await cartChange({
            id: lineKey,
            quantity: currentQty,
            selling_plan: targetPlanId,
          });
          await renderDrawer(drawer);
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        } catch (err) {
          console.error('[cart-drawer] subscribe toggle failed', err);
          input.checked = initialChecked;
        } finally {
          spinnerPop(drawer);
        }
      });
    });

    // Promo form
    const promoForm = drawer.querySelector('[data-cart-drawer-promo]');
    if (promoForm) {
      const promoInput = promoForm.querySelector('[data-cart-drawer-promo-input]');

      // Clear the inline error the moment the user starts editing again.
      if (promoInput) {
        promoInput.addEventListener('input', function () {
          const el = drawer.querySelector('[data-cart-drawer-promo-error]');
          if (el && !el.hidden) {
            el.textContent = '';
            el.hidden = true;
          }
        });
      }

      promoForm.addEventListener('submit', async function (event) {
        event.preventDefault();
        const input    = promoForm.querySelector('[data-cart-drawer-promo-input]');
        const errorEl  = drawer.querySelector('[data-cart-drawer-promo-error]');
        const code     = (input && input.value || '').trim();
        if (!code) return;
        spinnerPush(drawer);
        try {
          // Merge with any already-applied codes so we don't drop them.
          const current = await cartGet();
          const existing = (current.discount_codes || [])
            .filter(function (d) { return d.applicable; })
            .map(function (d) { return d.code; });
          if (existing.indexOf(code) === -1) existing.push(code);
          const updated = await cartUpdate({ discount: existing.join(',') });
          const applied = (updated.discount_codes || []).find(function (d) { return d.code.toLowerCase() === code.toLowerCase(); });
          if (!applied || !applied.applicable) {
            // The code came back but Shopify flagged it as not applicable.
            // Roll the cart back to the previous discount set so the bad code
            // doesn't linger, then surface the error inline. We deliberately
            // DO NOT re-render the section: the cart state didn't change, and
            // re-rendering would replace the DOM and wipe the error message
            // the customer needs to see.
            await cartUpdate({ discount: existing.filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); }).join(',') });
            if (errorEl) {
              errorEl.textContent = decodeEntities(drawer.dataset.promoInvalid) || "Code isn't valid. Try another.";
              errorEl.hidden = false;
            }
            return;
          }
          // Success path — clear the input and re-render so the chip shows.
          if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
          if (input) input.value = '';
          await renderDrawer(drawer);
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        } catch (err) {
          console.error('[cart-drawer] promo failed', err);
          if (errorEl) {
            errorEl.textContent = err.message || 'Could not apply the code.';
            errorEl.hidden = false;
          }
        } finally {
          spinnerPop(drawer);
        }
      });
    }

    // Promo remove
    drawer.querySelectorAll('[data-cart-drawer-promo-remove]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        const code = btn.dataset.code;
        spinnerPush(drawer);
        try {
          const current = await cartGet();
          const remaining = (current.discount_codes || [])
            .filter(function (d) { return d.applicable && d.code.toLowerCase() !== code.toLowerCase(); })
            .map(function (d) { return d.code; });
          await cartUpdate({ discount: remaining.join(',') });
          await renderDrawer(drawer);
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        } catch (err) {
          console.error('[cart-drawer] promo remove failed', err);
        } finally {
          spinnerPop(drawer);
        }
      });
    });

    // Recommended product "Add" buttons.
    // Split the add and the re-render into separate try/catch blocks: on some
    // privacy-hardened Android browsers the section-render fetch can fail
    // after a successful add. Without isolating them, the UI never refreshes
    // and the customer thinks "nothing happened" — they tap again and a
    // duplicate gets queued. We isolate so the badge / cart:updated event
    // always fires even when the optional re-paint hiccups.
    drawer.querySelectorAll('[data-cart-drawer-reco-add]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        const card = btn.closest('[data-cart-drawer-reco]');
        if (!card) return;
        const variantId = Number(card.dataset.variantId);
        if (!variantId) return;
        btn.disabled = true;
        spinnerPush(drawer);
        let added = false;
        try {
          await cartAdd({ id: variantId, quantity: 1 });
          added = true;
        } catch (err) {
          console.error('[cart-drawer] reco add failed', err);
          btn.disabled = false;
        }
        if (added) {
          try {
            await renderDrawer(drawer);
          } catch (err) {
            console.error('[cart-drawer] reco add re-render failed (item still added)', err);
          }
        }
        try {
          const cart = await cartGet();
          updateHeaderBadge(cart.item_count);
          if (added) {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          }
        } catch (e) { /* ignore badge refresh failure */ }
        spinnerPop(drawer);
      });
    });
  }

  // ── Custom element ─────────────────────────────────────────────────────
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      bindDrawerEvents(this);

      // Escape + Tab focus trap
      document.addEventListener('keydown', (event) => {
        if (this.getAttribute('aria-hidden') !== 'false') return;
        if (event.key === 'Escape') { closeDrawer(this); return; }
        if (event.key === 'Tab')    { trapFocus(this, event); }
      });
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  // ── Global wire-up ─────────────────────────────────────────────────────
  // 1. Document-level event API
  document.addEventListener('cart:open',  function () { openDrawer(); });
  document.addEventListener('cart:close', function () { closeDrawer(); });

  // 2. Header / any element with [data-cart-drawer-open]
  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-cart-drawer-open]');
    if (!trigger) return;
    event.preventDefault();
    openDrawer();
  });

  // 3. Public window API
  window.WillowCart = {
    open:  function ()         { openDrawer(); },
    close: function ()         { closeDrawer(); },
    add:   function (payload)  { return publicAdd(payload); },
    refresh: async function () {
      const drawer = document.querySelector(DRAWER_SELECTOR);
      if (!drawer) return;
      await renderDrawer(drawer);
      const cart = await cartGet();
      updateHeaderBadge(cart.item_count);
    },
  };

  // 4. Sync state on page show. The badge is updated unconditionally
  //    (cheap, and a defensive measure against any cookie/HTML divergence).
  //
  //    Bfcache problem: when the user goes to /checkout and presses Back,
  //    the browser may hand us back a frozen snapshot of the page from
  //    BEFORE their last cart mutation — e.g. they bumped a line from
  //    qty 2 → 1 right before hitting Checkout, then pressed Back, and
  //    the drawer markup still shows qty 2. We can't trust
  //    `event.persisted` to detect this: Safari and some Chrome builds
  //    fire pageshow on restore without setting it to true, so we'd miss
  //    the case. Instead we compare the badge count already painted in
  //    the drawer DOM to the canonical /cart.js count. Any mismatch
  //    means the markup is stale → force a Section Rendering API
  //    re-render. On normal navigations the counts already match so this
  //    is a no-op and costs nothing beyond the badge fetch.
  window.addEventListener('pageshow', function () {
    cartGet().then(function (cart) {
      updateHeaderBadge(cart.item_count);
      var drawer = document.querySelector(DRAWER_SELECTOR);
      if (!drawer) return;
      var badgeEl = drawer.querySelector('[data-cart-drawer-count]');
      var domCount = badgeEl ? parseInt(badgeEl.textContent, 10) : 0;
      if (domCount !== cart.item_count) {
        renderDrawer(drawer).catch(function (err) {
          console.error('[cart-drawer] pageshow re-render failed', err);
        });
      }
    }).catch(function () {});
  });

  // ── Analytics pixel bridge ─────────────────────────────────────────────
  // The Shopify Custom Pixel sandbox (assets/analytics-pixel.js, deployed
  // through Admin → Customer events) has no DOM access and no fetch to
  // /cart.js, so the theme has to push it everything line-item-aware:
  //
  //   1. `bundle_components_register` — once per page load, for every
  //      bundle product the page renders. The pixel caches the map and uses
  //      it to synthesise `component` rows in the webhook payload.
  //   2. `cart_snapshot` — after every cart mutation, with the full
  //      /cart.js payload so the pixel can read `_item_type` etc. from line
  //      properties (standard Customer Events strip them).
  //
  // We host the bridge here, not in product.js / FBT, because cart-drawer.js
  // is loaded on every page (the drawer is in layout/theme.liquid). One
  // place to wire up, no duplication.
  function pixelPublish(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (err) {
      console.warn('[cart-drawer] pixel publish failed', name, err);
    }
  }

  function publishBundleRegistrations() {
    const tags = document.querySelectorAll('script[data-bundle-components-register]');
    if (!tags.length) return;
    const merged = { bundles: {} };
    for (let i = 0; i < tags.length; i += 1) {
      try {
        const data = JSON.parse(tags[i].textContent || '{}');
        if (data && data.bundles) {
          Object.keys(data.bundles).forEach(function (id) {
            merged.bundles[id] = data.bundles[id];
          });
        }
      } catch (err) {
        console.warn('[cart-drawer] bundle payload parse failed', err);
      }
    }
    if (Object.keys(merged.bundles).length) pixelPublish('bundle_components_register', merged);
  }

  function publishCartSnapshot(cart) {
    if (!cart) return;
    pixelPublish('cart_snapshot', { cart: cart });
  }

  // After every cart mutation the drawer dispatches `cart:updated` with the
  // fresh /cart.js payload — piggyback on it instead of fetching again.
  document.addEventListener('cart:updated', function (event) {
    const cart = event && event.detail && event.detail.cart;
    if (cart) publishCartSnapshot(cart);
  });

  // Initial push — bundle map (DOM-driven) + current cart state.
  function bootstrapPixelBridge() {
    publishBundleRegistrations();
    cartGet().then(publishCartSnapshot).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapPixelBridge);
  } else {
    bootstrapPixelBridge();
  }
})();
